import React, { useState } from 'react';
import { Check, FileText, X } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import PaginationComponent from '../../../shared/components/PaginationComponent';
import { Field, StatusPill } from '../../../shared/components/ui/primitives';

/**
 * Pieces shared by Site Wallets (builder / Accounts) and My Wallet (holder).
 * Shapes are the server's presenters in housingram-backend
 * src/modules/siteWallet/controllers.ts.
 */

export const PAYMENT_MODES = [
  { value: 'BANK', label: 'Bank transfer' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CASH', label: 'Cash' }
];

export const LEDGER_TYPE_LABEL = {
  TOP_UP: 'Money added',
  EXPENSE: 'Expense',
  REVERSAL: 'Reversal',
  RETURN: 'Returned',
  ADJUSTMENT: 'Adjustment'
};

const STATUS_LABEL = {
  SUBMITTED: 'Waiting',
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
  REVERSED: 'Reversed',
  PAID: 'Paid',
  ACTIVE: 'Active',
  FROZEN: 'Frozen',
  CLOSED: 'Closed'
};

/** Frozen is not "over, without judgement": nothing new can go in. */
export const WalletStatusPill = ({ status }) => (
  <StatusPill status={status} tone={status === 'FROZEN' ? 'info' : undefined} dense>
    {STATUS_LABEL[status] ?? status}
  </StatusPill>
);

export const StatusChip = ({ status }) => (
  <StatusPill status={status} tone={status === 'REVERSED' ? 'danger' : undefined} dense>
    {STATUS_LABEL[status] ?? status}
  </StatusPill>
);

/** The own-pocket part once approved: DUE, PAID, NETTED or CANCELLED. */
export const ReimbursementChip = ({ expense }) => {
  const s = expense.reimbursement_status;
  if (!s || s === 'NONE') return null;
  const label = {
    DUE: 'Reimbursement due',
    PAID: 'Reimbursed',
    NETTED: 'Netted at settlement',
    CANCELLED: 'Reimbursement cancelled'
  }[s];
  return (
    <div className="mt-1 text-sm">
      <StatusPill status={s} dense>
        {label ?? s}
      </StatusPill>
      {s === 'PAID' && expense.reimbursed_reference && (
        <div className="mt-0.5 text-gray-500">
          {expense.reimbursed_mode} · {expense.reimbursed_reference}
        </div>
      )}
    </div>
  );
};

/** Operations Medium is a Manager, Low an Executive; same wallet rules for both. */
export const HolderName = ({ user }) => {
  if (!user) return <span className="text-gray-400">—</span>;
  const tag = user.level === 'Medium' ? 'Manager' : user.level === 'Low' ? 'Executive' : null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="font-medium text-gray-900">{user.name}</span>
      {tag && (
        <span className="rounded-pill bg-brand-100 px-2 py-0.5 text-[11px] font-medium text-brand-700">
          {tag}
        </span>
      )}
    </span>
  );
};

/** Bill photos open full size in a new tab; anything else is a file link. */
export const BillLinks = ({ urls = [] }) => {
  if (!urls.length) return <span className="text-sm text-gray-400">No bill</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {urls.map((url, i) =>
        /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(url) ? (
          <a key={url + i} href={url} target="_blank" rel="noreferrer" title="Open bill">
            <img
              src={url}
              alt={`Bill ${i + 1}`}
              className="h-12 w-12 rounded border border-gray-200 object-cover hover:opacity-80"
            />
          </a>
        ) : (
          <a
            key={url + i}
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-brand-700 hover:underline"
          >
            <FileText size={12} /> Bill {i + 1}
          </a>
        )
      )}
    </div>
  );
};

export const DecisionButtons = ({ disabled, onApprove, onReject, approveLabel = 'Approve' }) => (
  <div className="flex gap-2">
    <button
      type="button"
      disabled={disabled}
      onClick={onApprove}
      className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-brand-700 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Check size={14} /> {approveLabel}
    </button>
    <button
      type="button"
      disabled={disabled}
      onClick={onReject}
      className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-danger-border px-3.5 py-2 text-sm font-medium text-danger-fg hover:bg-danger-bg disabled:cursor-not-allowed disabled:opacity-40"
    >
      <X size={14} /> Reject
    </button>
  </div>
);

/** `size="md"` for a page's main actions; the default suits table rows. */
export const SmallButton = ({
  children,
  onClick,
  disabled,
  tone = 'default',
  title,
  size = 'sm'
}) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex items-center whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-40 ${
      size === 'md'
        ? 'gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium'
        : 'gap-1.5 rounded-lg px-3 py-1.5 text-sm'
    } ${
      tone === 'primary'
        ? 'bg-brand-700 text-white'
        : tone === 'danger'
          ? 'border border-danger-border text-danger-fg'
          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
    }`}
  >
    {children}
  </button>
);

const ModalActions = ({ onClose, busy, disabled, confirm, danger }) => (
  <div className="flex justify-end gap-2">
    <button
      type="button"
      onClick={onClose}
      className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
    >
      Cancel
    </button>
    <button
      type="submit"
      disabled={busy || disabled}
      className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
        danger ? 'bg-danger' : 'bg-brand-700'
      }`}
    >
      {busy ? 'Saving…' : confirm}
    </button>
  </div>
);

export const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-700 focus:outline-none';

/** A required reason: reject, reverse. The server wants at least 3 characters. */
export const NoteModal = ({
  title,
  label,
  placeholder,
  confirm,
  danger,
  busy,
  intro,
  minLength = 1,
  onClose,
  onSubmit
}) => {
  const [text, setText] = useState('');
  return (
    <Modal name={title} onClose={onClose} width={36}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(text.trim());
        }}
      >
        {intro && <p className="text-sm text-gray-600">{intro}</p>}
        <Field label={label} required>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            className={inputClass}
            placeholder={placeholder}
          />
        </Field>
        <ModalActions
          onClose={onClose}
          busy={busy}
          disabled={text.trim().length < minLength}
          confirm={confirm}
          danger={danger}
        />
      </form>
    </Modal>
  );
};

/** Accounts records how the money went: mode and UTR / reference. */
export const PaidModal = ({ title, intro, busy, confirm = 'Mark paid', onClose, onSubmit }) => {
  const [mode, setMode] = useState('BANK');
  const [reference, setReference] = useState('');
  return (
    <Modal name={title} onClose={onClose} width={36}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ mode, reference: reference.trim() || null });
        }}
      >
        {intro && <p className="text-sm text-gray-600">{intro}</p>}
        <Field label="Paid by" required>
          <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputClass}>
            {PAYMENT_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={
            mode === 'CASH' ? 'Reference / transaction ID (optional)' : 'Reference / transaction ID'
          }
        >
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            maxLength={200}
            className={inputClass}
            placeholder={
              mode === 'CASH' ? 'Voucher number' : 'UTR, UPI transaction ID or cheque number'
            }
          />
        </Field>
        <ModalActions onClose={onClose} busy={busy} confirm={confirm} />
      </form>
    </Modal>
  );
};

/** Mirrors the server's check; material goes through a material request. */
export function looksLikeMaterial(text) {
  return /\b(material|cement|sand|steel|aggregate|bricks?|tiles?)\b/i.test(text || '');
}

export function errorMessage(err, fallback) {
  return err?.response?.data?.message || fallback;
}

export function money(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '₹0';
  return `${n < 0 ? '−' : ''}₹${Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/** Axis labels: ₹1.2L reads at a glance where ₹120,000 does not. */
export function compactMoney(value) {
  const n = Number(value ?? 0);
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (Math.abs(n) >= 1000) return `₹${Math.round(n / 1000)}k`;
  return `₹${Math.round(n)}`;
}

export function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

/** YYYY-MM-DD in the viewer's own day, `days` from today. */
export function dayKey(days = 0) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-CA');
}

/** Same chip as ManagementHeaderTabs and Workforce. */
export const tabClass = (active) =>
  `rounded-md px-4 py-1.5 text-sm font-medium border transition-all duration-200 shrink-0 ${
    active
      ? 'bg-[#2A565E] text-white border-[#2A565E]'
      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
  }`;

/** Rows per page on the paged lists (expenses, ledgers). */
export const PAGE_SIZE = 50;

/**
 * Under a paged list: "Showing 51–100 of 612", and the repo's page controls
 * when there is more than one page. `meta` is the server's
 * { page, limit, total, pages }.
 */
export const ListFooter = ({ meta, page, onPageChange, isFetching }) => {
  const total = Number(meta?.total ?? 0);
  if (!total) return null;
  const limit = Number(meta?.limit) || PAGE_SIZE;
  const pages = Number(meta?.pages) || Math.ceil(total / limit);
  const first = (page - 1) * limit + 1;
  const last = Math.min(page * limit, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="text-sm text-gray-500">
        Showing {first.toLocaleString('en-IN')}–{last.toLocaleString('en-IN')} of{' '}
        {total.toLocaleString('en-IN')}
      </span>
      {pages > 1 && (
        <div className="flex-1">
          <PaginationComponent
            page={page}
            totalPages={pages}
            onPageChange={(p) => onPageChange(Math.min(Math.max(1, p), pages))}
            isFetching={isFetching}
          />
        </div>
      )}
    </div>
  );
};
